import { Components, Theme } from '@mui/material'

declare module '@mui/material/MenuItem' {
  interface MenuItemOwnProps {
    color?:
      | 'futureTheme.danger'
      // @deprecated - to be removed in favor of the semantic 'futureTheme.danger'
      | 'red'
  }
}

export const menuContainerStyle = (theme: Theme) => ({
  minWidth: 200,
  marginTop: theme.spacing(1.5),
  boxShadow: theme.futureTheme.shadows.sm,
  // Horizontal padding is on the container not the list, so it's in the right place for scrollbars
  padding: theme.spacing(0, 1),
  borderRadius: theme.shape['futureTheme.radius-md'],
  backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
  backgroundImage: 'none', // MUI applies a default background image for Paper
  '& .MuiMenu-list': menuListStyle(theme),
})

export const menuListStyle = (theme: Theme) => ({
  // Vertical padding is on the list, not on the container, so that if we scroll we scroll off the top of the container
  padding: theme.spacing(1, 0),

  // Dividers within menus don't go full width
  '& .MuiMenuItem-root + .MuiDivider-root': {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },

  // Menu subheaders
  '& .MuiListSubheader-root': {
    ...theme.typography['futureTheme.small-regular'],
    color: theme.palette.futureTheme.text.subtle,
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
    // Increase the top pad, but offset with a negative margin
    // This means that the text doesn't move up at all when sticky
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(-1),
  },
})

export const MuiMenu: Components<Omit<Theme, 'components'>>['MuiMenu'] = {
  styleOverrides: {
    paper: ({ theme }) => menuContainerStyle(theme),
  },
}

export const MuiMenuItem: Components<Omit<Theme, 'components'>>['MuiMenuItem'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // Duplicated in MuiAutocomplete option
      borderRadius: theme.shape['futureTheme.radius-sm'],
      padding: theme.spacing(0.5, 2),
      minHeight: '28px',
      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.default,
      '&:hover': {
        backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
      },
      '&:not(:last-child)': {
        marginBottom: theme.spacing(0.5),
      },
      '&.Mui-disabled': {
        opacity: 0.4,
      },

      // Consistent (smaller) icon size and spacing
      '& .MuiListItemIcon-root': {
        minWidth: 18,
        width: 18,
        marginRight: theme.spacing(2),
        color: theme.palette.futureTheme.fg.default,
        '& > svg': {
          fontSize: 18,
        },
      },
    }),
  },
  variants: [
    {
      // Handle the deprecated "red" color
      props: (props) => props.color === 'futureTheme.danger' || props.color === 'red',
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.danger.default,
        '& .MuiListItemIcon-root': {
          color: theme.palette.futureTheme.fg.danger.default,
        },
      }),
    },
  ],
}
